import { useState, useEffect, useRef, createContext, useContext, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import toast from 'react-hot-toast'
import { API_BASE_URL } from '../config/api'
import SessionExpiryBanner from '../components/SessionExpiryBanner'
import {
  ACTIVITY_THROTTLE_MS,
  EVENT_AUTH_CLEARED,
  EVENT_SESSION_EXPIRED,
  EVENT_SESSION_LOGOUT,
  SESSION_CHANNEL,
  STORAGE_LAST_ACTIVITY,
  broadcastSession,
  clearSessionMeta,
  createIdleMonitor,
  getAuthToken,
  isEditorRoute,
  markLocalActivity,
  readLastActivity,
  saveSessionMeta,
  shouldDeferHardRedirect,
} from '../utils/sessionIdle'
import { clearAllPdfSignerEditorDrafts } from '../utils/editorDraft'

const AuthContext = createContext()

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  // Inicializar estado desde localStorage o sessionStorage si existe
  const getInitialUser = () => {
    try {
      // Verificar primero localStorage (sesión persistente)
      let userData = localStorage.getItem('user')
      if (!userData) {
        // Si no hay en localStorage, verificar sessionStorage (sesión temporal)
        userData = sessionStorage.getItem('user')
      }
      if (userData) {
        return JSON.parse(userData)
      }
    } catch (error) {
      console.error('Error al parsear datos de usuario:', error)
    }
    return null
  }

  const [user, setUser] = useState(getInitialUser())
  const [isLoading, setIsLoading] = useState(true)
  const [sessionBanner, setSessionBanner] = useState(null) // 'warning' | 'expired' | null
  const navigate = useNavigate()
  const location = useLocation()
  const idleMonitorRef = useRef(null)
  const activityInFlightRef = useRef(false)
  const lastActivityPostRef = useRef(0)

  // Lista de rutas públicas que no requieren autenticación
  const publicRoutes = ['/login', '/registrar', '/firmar', '/firma-exitosa', '/reset-password']
  
  const isPublicRoute = (pathname) => {
    return publicRoutes.some(route => pathname.startsWith(route))
  }

  const clearAuthStorage = useCallback(() => {
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    sessionStorage.removeItem('token')
    sessionStorage.removeItem('user')
    clearSessionMeta()
  }, [])

  const postHumanActivity = useCallback(async () => {
    const token = getAuthToken()
    if (!token || activityInFlightRef.current) return null
    const now = Date.now()
    if (now - lastActivityPostRef.current < ACTIVITY_THROTTLE_MS) return null
    activityInFlightRef.current = true
    lastActivityPostRef.current = now
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/activity`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      })
      if (response.status === 401) {
        window.dispatchEvent(new CustomEvent(EVENT_SESSION_EXPIRED, { detail: { source: 'activity' } }))
        return null
      }
      if (!response.ok) return null
      return await response.json()
    } catch {
      return null
    } finally {
      activityInFlightRef.current = false
    }
  }, [])

  // Función para verificar si el modal está abierto
  const isModalOpen = () => {
    try {
      const saved = sessionStorage.getItem('pdfEditor_showShareModal')
      return saved === 'true'
    } catch (e) {
      return false
    }
  }

  useEffect(() => {
    // CRÍTICO: NO verificar autenticación si el modal de compartir está abierto
    // Verificar INMEDIATAMENTE antes de hacer cualquier cosa
    const modalCheck = isModalOpen()
    
    if (modalCheck) {
      setIsLoading(false)
      return
    }

    // Verificar múltiples veces con delays para evitar race conditions
    const checkModalMultipleTimes = async () => {
      // Verificación 1: inmediata
      if (isModalOpen()) {
        setIsLoading(false)
        return
      }
      
      // Verificación 2: después de 50ms
      await new Promise(resolve => setTimeout(resolve, 50))
      if (isModalOpen()) {
        setIsLoading(false)
        return
      }
      
      // Verificación 3: después de 100ms
      await new Promise(resolve => setTimeout(resolve, 50))
      if (isModalOpen()) {
        setIsLoading(false)
        return
      }
      
      // Verificación 4: después de 200ms
      await new Promise(resolve => setTimeout(resolve, 100))
      if (isModalOpen()) {
        setIsLoading(false)
        return
      }
      
      // Si llegamos aquí, el modal NO está abierto, proceder con checkAuth
      
      // Si estamos en una ruta pública (login), verificar si hay token
      if (isPublicRoute(location.pathname)) {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token')
        const userData = localStorage.getItem('user') || sessionStorage.getItem('user')
        
        if (!token || !userData) {
          setIsLoading(false)
          setUser(null)
          return
        }
        
        // Verificar modal una vez más antes de llamar checkAuth
        if (isModalOpen()) {
          setIsLoading(false)
          return
        }
        checkAuth()
        return
      }
      
      // Solo verificar autenticación en rutas protegidas
      // Verificar modal una vez más antes de llamar checkAuth
      if (isModalOpen()) {
        setIsLoading(false)
        return
      }
      checkAuth()
    }
    
    checkModalMultipleTimes()
  }, [location.pathname])

  const checkAuth = async () => {
    // ============================================
    // CRÍTICO: PRIMERA VERIFICACIÓN - ANTES DE CUALQUIER COSA
    // ============================================
    // Verificar sessionStorage DIRECTAMENTE - ESTA ES LA PRIMERA LÍNEA DE CÓDIGO
    // NO HACER NADA ANTES DE ESTO - NI SIQUIERA console.log
    try {
      // Verificación 1: inmediata y síncrona
      const modalValue1 = sessionStorage.getItem('pdfEditor_showShareModal')
      if (modalValue1 === 'true') {
        console.log('[checkAuth] 🚫🚫🚫 BLOQUEANDO INMEDIATAMENTE - modal detectado en sessionStorage (verificación 1)')
        setIsLoading(false)
        return
      }
      
      // Verificación 2: después de 5ms
      await new Promise(resolve => setTimeout(resolve, 5))
      const modalValue2 = sessionStorage.getItem('pdfEditor_showShareModal')
      if (modalValue2 === 'true') {
        console.log('[checkAuth] 🚫🚫🚫 BLOQUEANDO - modal detectado (verificación 2 - 5ms)')
        setIsLoading(false)
        return
      }
      
      // Verificación 3: después de 20ms
      await new Promise(resolve => setTimeout(resolve, 15))
      const modalValue3 = sessionStorage.getItem('pdfEditor_showShareModal')
      if (modalValue3 === 'true') {
        console.log('[checkAuth] 🚫🚫🚫 BLOQUEANDO - modal detectado (verificación 3 - 20ms)')
        setIsLoading(false)
        return
      }
      
      // Verificación 4: después de 50ms
      await new Promise(resolve => setTimeout(resolve, 30))
      const modalValue4 = sessionStorage.getItem('pdfEditor_showShareModal')
      if (modalValue4 === 'true') {
        console.log('[checkAuth] 🚫🚫🚫 BLOQUEANDO - modal detectado (verificación 4 - 50ms)')
        setIsLoading(false)
        return
      }
      
      // Verificación 5: después de 100ms
      await new Promise(resolve => setTimeout(resolve, 50))
      const modalValue5 = sessionStorage.getItem('pdfEditor_showShareModal')
      if (modalValue5 === 'true') {
        console.log('[checkAuth] 🚫🚫🚫 BLOQUEANDO - modal detectado (verificación 5 - 100ms)')
        setIsLoading(false)
        return
      }
      
      console.log('[checkAuth] ✅ Todas las verificaciones pasadas, modal NO detectado, continuando con checkAuth...')
    } catch (e) {
      console.error('[checkAuth] Error al verificar sessionStorage:', e)
    }
    
    // Verificar también con la función isModalOpen() para doble verificación
    const modalCheck1 = isModalOpen()
    console.log('[checkAuth] 🔍 Verificación 1 del modal (con función):', modalCheck1)
    if (modalCheck1) {
      console.log('[checkAuth] 🚫 BLOQUEANDO verificación de autenticación - modal de compartir está abierto (inicio)')
      return
    }
    
    // Verificar nuevamente después de un pequeño delay
    await new Promise(resolve => setTimeout(resolve, 10))
    const modalCheck2 = sessionStorage.getItem('pdfEditor_showShareModal') === 'true'
    console.log('[checkAuth] 🔍 Verificación 2 del modal (después de 10ms):', modalCheck2)
    if (modalCheck2) {
      console.log('[checkAuth] 🚫 BLOQUEANDO verificación de autenticación - modal de compartir está abierto (después de 10ms)')
      return
    }
    
    // Verificar una tercera vez después de otro delay
    await new Promise(resolve => setTimeout(resolve, 50))
    const modalCheck3 = sessionStorage.getItem('pdfEditor_showShareModal') === 'true'
    console.log('[checkAuth] 🔍 Verificación 3 del modal (después de 50ms):', modalCheck3)
    if (modalCheck3) {
      console.log('[checkAuth] 🚫 BLOQUEANDO verificación de autenticación - modal de compartir está abierto (después de 50ms)')
      return
    }

    // Verificar primero localStorage (sesión persistente)
    let token = localStorage.getItem('token')
    let userData = localStorage.getItem('user')
    let storageType = 'localStorage'
    
    // Si no hay en localStorage, verificar sessionStorage (sesión temporal)
    if (!token || !userData) {
      token = sessionStorage.getItem('token')
      userData = sessionStorage.getItem('user')
      storageType = 'sessionStorage'
    }

    // CRÍTICO: Verificar modal ANTES de hacer cualquier cosa más
    if (sessionStorage.getItem('pdfEditor_showShareModal') === 'true') {
      setIsLoading(false)
      return
    }

    // CRÍTICO: Verificar modal UNA VEZ MÁS antes de este log
    if (sessionStorage.getItem('pdfEditor_showShareModal') === 'true') {
      setIsLoading(false)
      return
    }

    // Si no hay token o datos de usuario en ningún storage, limpiar estado
    if (!token || !userData) {
      console.log('[checkAuth] No hay token o datos de usuario, limpiando estado')
      setUser(null)
      setIsLoading(false)
      return
    }

    try {
      // Verificar si el token sigue siendo válido
      // console.log('[checkAuth] Verificando token con backend...')
      
      // Verificar modal una vez más ANTES de hacer la llamada al backend
      if (isModalOpen()) {
        setIsLoading(false)
        return
      }
      
      // Verificar que el token no esté vacío o mal formado
      if (!token || token.trim() === '') {
        console.log('[checkAuth] Token vacío o inválido, limpiando estado')
        localStorage.removeItem('token')
        localStorage.removeItem('user')
        sessionStorage.removeItem('token')
        sessionStorage.removeItem('user')
        setUser(null)
        setIsLoading(false)
        return
      }
      
      const response = await fetch(`${API_BASE_URL}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      // Verificar modal DESPUÉS de la llamada también
      if (isModalOpen()) {
        setIsLoading(false)
        return
      }

      if (response.ok) {
        const userInfo = await response.json()
        // console.log('[checkAuth] Token válido, usuario autenticado:', userInfo.email)
        setUser(userInfo)
        // Actualizar en el mismo storage que se encontró
        if (storageType === 'localStorage') {
          localStorage.setItem('user', JSON.stringify(userInfo))
        } else {
          sessionStorage.setItem('user', JSON.stringify(userInfo))
        }
      } else if (response.status === 401) {
        // Token inválido o expirado - esto es normal y esperado
        // No loguear como error, solo limpiar el estado silenciosamente
        try {
          await response.json() // Consumir el body para evitar warnings
        } catch (e) {
          // Ignorar errores al leer el body
        }
        // Limpiar ambos storages silenciosamente
        localStorage.removeItem('token')
        localStorage.removeItem('user')
        sessionStorage.removeItem('token')
        sessionStorage.removeItem('user')
        setUser(null)
      } else {
        // Otro error HTTP
        console.warn('[checkAuth] Error al verificar token:', response.status, response.statusText)
        // Limpiar storages por seguridad
        localStorage.removeItem('token')
        localStorage.removeItem('user')
        sessionStorage.removeItem('token')
        sessionStorage.removeItem('user')
        setUser(null)
      }
    } catch (error) {
      // Solo loguear errores de red, no errores 401 esperados
      if (error.name !== 'TypeError' || !error.message.includes('fetch')) {
        console.error('[checkAuth] Error al verificar autenticación:', error)
      }
      // Limpiar ambos storages en caso de error
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      sessionStorage.removeItem('token')
      sessionStorage.removeItem('user')
      setUser(null)
    } finally {
      setIsLoading(false)
    }
  }

  const login = async (email, password, rememberMe = false) => {
    try {
      console.log('[login] Iniciando sesión...', { email, rememberMe })
      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          email, 
          password,
          remember_me: rememberMe
        }),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.detail || 'Error al iniciar sesión')
      }

      const data = await response.json()
      console.log('[login] Login exitoso, guardando en:', rememberMe ? 'localStorage' : 'sessionStorage')
      
      // Limpiar ambos storages antes de guardar
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      sessionStorage.removeItem('token')
      sessionStorage.removeItem('user')
      
      // Guardar token y datos del usuario según preferencia
      if (rememberMe) {
        localStorage.setItem('token', data.access_token)
        localStorage.setItem('user', JSON.stringify(data.user))
        console.log('[login] Token y datos guardados en localStorage (sesión persistente)')
      } else {
        sessionStorage.setItem('token', data.access_token)
        sessionStorage.setItem('user', JSON.stringify(data.user))
        console.log('[login] Token y datos guardados en sessionStorage (sesión temporal)')
      }

      if (data.user?.session_policy) {
        saveSessionMeta(data.user.session_policy, rememberMe)
      }
      markLocalActivity(Date.now())
      setSessionBanner(null)
      
      setUser(data.user)
      
      // Verificar si requiere cambio de contraseña
      const requiresPasswordChange = data.user.must_change_password || data.user.requires_password_change
      
      if (requiresPasswordChange) {
        toast.success(`¡Bienvenido, ${data.user.nombre}! Debes cambiar tu contraseña temporal.`)
        return { ...data, requires_password_change: true }
      }
      
      toast.success(`¡Bienvenido, ${data.user.nombre}!`)
      return data
      
    } catch (error) {
      console.error('[login] Error al iniciar sesión:', error)
      toast.error(error.message)
      throw error
    }
  }

  const logout = async ({ reason = 'manual', skipNavigate = false } = {}) => {
    let token = localStorage.getItem('token')
    if (!token) {
      token = sessionStorage.getItem('token')
    }

    if (idleMonitorRef.current) {
      idleMonitorRef.current.stop()
      idleMonitorRef.current = null
    }

    // Aviso de logout (p. ej. monitores idle). Sin persistencia/recuperación de borradores.
    window.dispatchEvent(new CustomEvent(EVENT_SESSION_LOGOUT, { detail: { reason } }))
    clearAllPdfSignerEditorDrafts()

    setUser(null)
    setIsLoading(false)
    setSessionBanner(null)
    clearAuthStorage()
    broadcastSession('logout', { reason })
    window.dispatchEvent(new CustomEvent(EVENT_AUTH_CLEARED, { detail: { reason } }))

    try {
      if (token) {
        await fetch(`${API_BASE_URL}/api/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })
      }
    } catch (error) {
      console.error('Error al cerrar sesión:', error)
    }

    if (reason === 'manual') {
      toast.success('Sesión cerrada exitosamente')
    } else if (reason === 'idle') {
      toast.error('Sesión cerrada por inactividad')
    }

    const defer = skipNavigate || shouldDeferHardRedirect(location.pathname)
    if (defer && isEditorRoute(location.pathname)) {
      setSessionBanner('expired')
      return
    }
    if (!skipNavigate) {
      navigate('/login', { replace: true })
    }
  }

  const getToken = () => {
    return localStorage.getItem('token') || sessionStorage.getItem('token')
  }

  const isAuthenticated = () => {
    return !!user
  }

  const isAdmin = () => {
    return user?.is_admin || false
  }

  // Monitor de inactividad + sync entre pestañas (no renueva por checkAuth/polling)
  useEffect(() => {
    if (!user) {
      if (idleMonitorRef.current) {
        idleMonitorRef.current.stop()
        idleMonitorRef.current = null
      }
      return undefined
    }

    const monitor = createIdleMonitor({
      onHumanActivity: () => {
        postHumanActivity()
      },
      onWarning: () => {
        setSessionBanner('warning')
        window.dispatchEvent(new CustomEvent('pdfsigner:session-warning'))
      },
      onExpired: () => {
        setSessionBanner('expired')
        window.dispatchEvent(new CustomEvent(EVENT_SESSION_EXPIRED, { detail: { source: 'idle' } }))
        logout({ reason: 'idle' })
      },
    })
    idleMonitorRef.current = monitor
    monitor.start()

    let channel
    try {
      channel = new BroadcastChannel(SESSION_CHANNEL)
      channel.onmessage = (event) => {
        const data = event.data || {}
        if (data.type === 'activity' && data.at) {
          // Solo sincroniza reloj local; no llama /activity (evita prolongar sin humano local)
          const local = readLastActivity()
          if (data.at > local) {
            localStorage.setItem(STORAGE_LAST_ACTIVITY, String(data.at))
          }
        }
        if (data.type === 'logout') {
          setUser(null)
          clearAuthStorage()
          setSessionBanner(null)
          if (!shouldDeferHardRedirect(location.pathname)) {
            navigate('/login', { replace: true })
          } else {
            setSessionBanner('expired')
          }
        }
      }
    } catch {
      channel = null
    }

    const onStorage = (e) => {
      if (e.key === STORAGE_LAST_ACTIVITY && e.newValue) {
        // activity sync via storage
      }
      if (e.key === 'pdfsigner_session_broadcast' && e.newValue) {
        try {
          const data = JSON.parse(e.newValue)
          if (data.type === 'logout') {
            setUser(null)
            clearAuthStorage()
            if (!shouldDeferHardRedirect(location.pathname)) navigate('/login', { replace: true })
            else setSessionBanner('expired')
          }
        } catch { /* ignore */ }
      }
    }
    window.addEventListener('storage', onStorage)

    const onExpiredEvent = (e) => {
      if (e?.detail?.source === 'activity' || e?.detail?.source === 'axios') {
        setSessionBanner('expired')
        logout({ reason: 'expired', skipNavigate: shouldDeferHardRedirect(location.pathname) })
      }
    }
    window.addEventListener(EVENT_SESSION_EXPIRED, onExpiredEvent)

    return () => {
      monitor.stop()
      idleMonitorRef.current = null
      if (channel) channel.close()
      window.removeEventListener('storage', onStorage)
      window.removeEventListener(EVENT_SESSION_EXPIRED, onExpiredEvent)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id])

  const value = {
    user,
    isLoading,
    login,
    logout,
    getToken,
    isAuthenticated,
    isAdmin,
    checkAuth,
    postHumanActivity,
  }

  return (
    <AuthContext.Provider value={value}>
      <SessionExpiryBanner
        mode={sessionBanner}
        onContinue={() => {
          setSessionBanner(null)
          idleMonitorRef.current?.resetAfterContinue?.()
          markLocalActivity(Date.now())
          postHumanActivity()
        }}
        onLogout={() => logout({ reason: sessionBanner === 'expired' ? 'expired' : 'manual', skipNavigate: false })}
      />
      {children}
    </AuthContext.Provider>
  )
}
